// Validation schema here (object mix from all necessary fields) for step01
import { z } from 'zod';

import { FieldTypes } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { MIN_BONUS_IMPRESSIONS_VALUE } from '@features/campaigns/builder/consts/common.ts';
import {
  SWITCH_IN_BOTTOM_CREATIVE_DIMENSIONS,
  SWITCH_IN_LEFT_CREATIVE_DIMENSIONS,
} from '@features/campaigns/builder/utils/areCreativeDimensionsValid.ts';
import {
  creativeImageFileValidationCheck,
  creativeVideoFileValidationCheck,
} from '@features/campaigns/builder/utils/creativeFileValidationCheck.ts';
import { createOptionalHttpUrlFieldValidator } from '@features/campaigns/builder/utils/httpUrlValidators.ts';
import {
  ALLOWED_VIDEO_FORMATS,
  CREATIVE_FORMAT_IMAGE_PNG,
  LANDING_CREATIVE_ALLOWED_FORMATS,
  SWITCH_IN_CREATIVE_ALLOWED_FORMATS,
} from '@features/campaigns/builder/utils/isCreativeFormatValid.ts';
import { resolveSwitchInMaxFileSize } from '@features/campaigns/builder/utils/isCreativeSizeValid.ts';
import { numberOfCreativesValidationCheck } from '@features/campaigns/builder/utils/numberOfCreativesValidationCheck.ts';
import {
  AgeGender,
  CampaignMediaType,
  CampaignStatus,
  CampaignType,
  GenericTargetCollectionItem,
  IncludeOperation,
  InvalidSectionsItem,
  InterestSegments,
  Operation,
  Provinces,
} from '@features/campaigns/shared/types/campaign';
import { AccessRight } from '@shared/api/commonApiInterfaces';
import { CampaignId } from '@shared/types/campaign.ts';
import { UserCountryCode } from '@shared/types/countryCodes.ts';

const step01CommonValidationSchema = {
  [FieldTypes.BUDGET]: z
    .number({
      required_error: 'errorMessages.required',
      invalid_type_error: 'errorMessages.numericalValueRequired',
    })
    .min(1, 'errorMessages.minBudgetRequired')
    .max(5000000, 'errorMessages.maxBudgetRequired'),
  [FieldTypes.DATERANGE]: z
    .array(
      z.date({
        required_error: 'errorMessages.required',
        invalid_type_error: 'errorMessages.dateRangeProvideEndDate',
      }),
    )
    .length(2, 'errorMessages.dateRangeLength'),
  [FieldTypes.CAMPAIGN_NAME]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .min(1, 'errorMessages.required')
    .refine((value) => value.trim().length > 0, 'errorMessages.atLeastOneCharacterRequired'),
};

export const step01validationSchema = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.BRAND]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.AGENCY_COMMISSION]: z
    .boolean({
      required_error: 'errorMessages.required',
    })
    .optional(),
  [FieldTypes.IS_NAME_AUTOGENERATED]: z.boolean(),
});

export enum TreeItemType {
  City = 'city',
  Province = 'province',
}

export type CreateCampaignFormStep01Values = z.infer<typeof step01validationSchema>;

export const step01ExternalValidationSchemaInitializationStep = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z.string().optional(),
  [FieldTypes.BILLING_CONTACT]: z.string().optional(),
});

export const step01ExternalValidationSchema = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.BILLING_CONTACT]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
});

export type CreateCampaignFormStep01ExternalValues = z.infer<typeof step01ExternalValidationSchema>;
export type CreateCampaignFormStep01ExternalValuesOptionalFields = z.infer<
  typeof step01ExternalValidationSchemaInitializationStep
>;

export interface FormPageStepProps<T> {
  campaignId: CampaignId;
  campaignType: CampaignType;
  mediaType?: CampaignMediaType;
  stepSavedData: T;
  accessRights?: AccessRight[];
}

export interface RestoredStep01Data extends CreateCampaignFormStep01Values {
  id: CampaignId;
  advertiserName: string;
  advertiserCode: string;
  brandName: string;
  campaignType: CampaignType;
  brandCode: string;
  country: UserCountryCode;
  status: CampaignStatus;
  createdBy: string;
  invalidSections?: InvalidSectionsItem[];
}

export interface RestoredStep01ExternalData extends CreateCampaignFormStep01ExternalValuesOptionalFields {
  id: CampaignId;
  advertiserName?: string;
  advertiserCode?: string;
  billingContactName?: string;
  billingContactCode?: string;
  status: CampaignStatus;
  createdBy: string;
  invalidSections?: InvalidSectionsItem[];
}

export interface FormPageInitialStepProps {
  campaignId?: CampaignId;
  mediaType?: CampaignMediaType;
  stepSavedData?: RestoredStep01Data;
  accessRights?: AccessRight[];
}

export interface CreateCampaignFormStep02Values {
  contentPackage: GenericTargetCollectionItem | null;
  provinces: Provinces | null;
  cities: Provinces | null;
  ageGender: AgeGender | null;
  interestSegments: InterestSegments | null;
  provincesOperationType: Operation | null;
  ageAndGenderOperationType: Operation | null;
  interestSegmentsOperationType: Operation | null;
  contentPackageOperationType: IncludeOperation | null;
}

export const step03validationSchema = z
  .object({
    [FieldTypes.FORMAT_TYPE_VIDEO]: z.boolean({}).optional(),
    [FieldTypes.FORMAT_TYPE_DISPLAY]: z.boolean({}).optional(),
    [FieldTypes.IS_CREATIVE_SERVICES_FULL]: z.boolean({}).optional(),
    [FieldTypes.NUMBER_OF_CREATIVES]: z
      .number({ invalid_type_error: 'errorMessages.numberOfCreativesWrongType' })
      .optional(),
    [FieldTypes.VIDEO_PRODUCTS_LINEAR_ADDRESSABLE]: z.boolean({}).optional(),
    [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) =>
        creativeImageFileValidationCheck(value, ctx, { allowedFormats: [CREATIVE_FORMAT_IMAGE_PNG] }),
      ),
    [FieldTypes.CREATIVE_LANDING_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) =>
        creativeImageFileValidationCheck(value, ctx, { allowedFormats: LANDING_CREATIVE_ALLOWED_FORMATS }),
      ),
  })
  .superRefine(({ formatTypeDisplay, isCreativeServicesFull, numberOfCreatives }, ctx) =>
    numberOfCreativesValidationCheck({ formatTypeDisplay, isCreativeServicesFull, numberOfCreatives }, ctx),
  );

export type UpdateCampaignFormStep03Values = z.infer<typeof step03validationSchema>;

export const step03ExternalValidationSchema = z
  .object({
    [FieldTypes.FORMAT_TYPE_VIDEO]: z.boolean({}).optional(),
    [FieldTypes.FORMAT_TYPE_DISPLAY]: z.boolean({}).optional(),
    [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) =>
        creativeImageFileValidationCheck(value, ctx, { allowedFormats: [CREATIVE_FORMAT_IMAGE_PNG] }),
      ),
    [FieldTypes.CREATIVE_LANDING_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) =>
        creativeImageFileValidationCheck(value, ctx, { allowedFormats: LANDING_CREATIVE_ALLOWED_FORMATS }),
      ),
    [FieldTypes.CREATIVE_VIDEO_SPOT]: z
      .any()
      .superRefine(async (value, ctx) => creativeVideoFileValidationCheck(value, ctx, [ALLOWED_VIDEO_FORMATS])),
    [FieldTypes.TRACKING_URL]: createOptionalHttpUrlFieldValidator(),
    [FieldTypes.VIDEO_CONTAINS_POLITICAL_CONTENT]: z.boolean().optional(),
    [FieldTypes.LSHAPE_CONTAINS_POLITICAL_CONTENT]: z.boolean().optional(),
    [FieldTypes.LANDING_CONTAINS_POLITICAL_CONTENT]: z.boolean().optional(),
    [FieldTypes.CREATIVE_SWITCH_IN_LEFT]: z.any().superRefine(async (value, ctx) =>
      creativeImageFileValidationCheck(value, ctx, {
        allowedFormats: SWITCH_IN_CREATIVE_ALLOWED_FORMATS,
        dimensions: SWITCH_IN_LEFT_CREATIVE_DIMENSIONS,
        resolveMaxFileSize: resolveSwitchInMaxFileSize,
      }),
    ),
    [FieldTypes.CREATIVE_SWITCH_IN_BOTTOM]: z.any().superRefine(async (value, ctx) =>
      creativeImageFileValidationCheck(value, ctx, {
        allowedFormats: SWITCH_IN_CREATIVE_ALLOWED_FORMATS,
        dimensions: SWITCH_IN_BOTTOM_CREATIVE_DIMENSIONS,
        resolveMaxFileSize: resolveSwitchInMaxFileSize,
      }),
    ),
    [FieldTypes.SWITCH_IN_LEFT_CONTAINS_POLITICAL_CONTENT]: z.boolean().optional(),
    [FieldTypes.SWITCH_IN_BOTTOM_CONTAINS_POLITICAL_CONTENT]: z.boolean().optional(),
  })

  .superRefine(({ trackingUrl, formatTypeVideo, creativeVideoSpot }, ctx) => {
    if (formatTypeVideo && creativeVideoSpot && !trackingUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorMessages.required',
        path: [FieldTypes.TRACKING_URL],
      });
    }
  });

export type UpdateCampaignFormStep03ExternalValues = z.infer<typeof step03ExternalValidationSchema>;

export const step04validationSchema = z
  .object({
    [FieldTypes.IMPRESSIONS]: z.number({ invalid_type_error: 'errorMessages.numericalValueRequired' }).optional(),
    [FieldTypes.BONUS_PRICE]: z.boolean().optional(),
    [FieldTypes.INVENTORY]: z.boolean().optional(),
    [FieldTypes.BONUS_IMPRESSIONS_TOGGLE]: z.boolean().optional(),
    [FieldTypes.BONUS_IMPRESSIONS]: z
      .number({ invalid_type_error: 'errorMessages.numericalValueRequired' })
      .min(MIN_BONUS_IMPRESSIONS_VALUE, 'errorMessages.minBonusImpressionsValue')
      .nullable()
      .optional(),
    [FieldTypes.CPM]: z.number({ invalid_type_error: 'errorMessages.numericalValueRequired' }).optional(),
    [FieldTypes.ADDITIONAL_NOTE]: z.string().max(5000, 'errorMessages.maxAdditionalNoteLength').optional(),
    [FieldTypes.BUDGET]: z
      .number({
        required_error: 'errorMessages.required',
        invalid_type_error: 'errorMessages.numericalValueRequired',
      })
      .optional(),
  })
  .superRefine(({ bonusImpressionsToggle, bonusImpressions }, ctx) => {
    if (bonusImpressionsToggle && bonusImpressions === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorMessages.numericalValueRequired',
        path: [FieldTypes.BONUS_IMPRESSIONS],
      });
    }
  });

export type UpdateCampaignFormStep04Values = z.infer<typeof step04validationSchema>;
